/**
 * The System → Users list page's filter + sort declaration — a consumer of the
 * generic filter toolkit (`components/filters`), mirroring `studentsFilterConfig`.
 *
 * Facets exposed:
 *   - search     → `q`                       (debounced free text: email or name)
 *   - status     → `status`                  multi-select over
 *                                            active/inactive/archived/pending,
 *                                            allEncoding 'all'. The page's prior
 *                                            default chip was "All", so the
 *                                            default value here is `[]` which,
 *                                            with allEncoding 'all', explicitly
 *                                            emits `status=all`. Under the
 *                                            students-style contract an *omitted*
 *                                            status would mean active-only, so
 *                                            "All" must be sent literally to keep
 *                                            the prior user-visible default.
 *   - role       → `role`                    multi-select over the 10 role slugs,
 *                                            comma-joined; allEncoding 'omit'
 *                                            (no role param = all roles).
 *   - lastLogin  → `lastLoginFrom`/`lastLoginTo` date range (last sign-in).
 *
 * Sorts map to the backend's allow-listed `sort` fields
 * (createdAt|lastLoginAt|displayName). The default (first entry) is
 * "Recently added" = createdAt:desc — the page previously listed newest-first.
 *
 * Sortable column headers: Name (displayName) and Last login (lastLoginAt). The
 * createdAt sorts are dropdown-only (the table has no "Added" column).
 */

import { z } from 'zod';
import type { Role } from '../../lib/api';
import { formatRoleLabel } from './RoleBadge';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

/**
 * Every assignable portal role slug, in the order surfaced by the Role filter.
 * Labels reuse the shared {@link formatRoleLabel} helper so the filter menu and
 * the row's {@link RoleBadge} always read identically.
 */
const ROLE_SLUGS: ReadonlyArray<Role> = [
  'super_admin',
  'academic_admin',
  'finance_admin',
  'nalim',
  'teacher',
  'parent',
  'student',
  'support_viewer',
  'candidate',
  'hr',
];

export const usersFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'search',
      key: 'q',
      label: 'Search',
      placeholder: 'Search by email or name',
    },
    {
      kind: 'multi-select',
      key: 'status',
      label: 'Status',
      group: 'filters',
      // Prior page default was the "All" chip. Empty picks + allEncoding 'all'
      // reproduces that: the toolkit emits `status=all` rather than omitting it.
      defaultValue: [],
      allEncoding: 'all',
      options: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
        { value: 'archived', label: 'Archived' },
        { value: 'pending', label: 'Pending' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'role',
      label: 'Role',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: ROLE_SLUGS.map((slug) => ({ value: slug, label: formatRoleLabel(slug) })),
    },
    {
      kind: 'date-range',
      key: 'lastLogin',
      label: 'Last sign-in',
      group: 'filters',
      params: { from: 'lastLoginFrom', to: 'lastLoginTo' },
    },
  ],
  sorts: [
    { value: 'createdAt:desc', label: 'Recently added' },
    { value: 'createdAt:asc', label: 'Oldest first' },
    { value: 'lastLoginAt:desc', label: 'Recent sign-in' },
    // Registered so the Last-login sortable header can toggle to ascending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'lastLoginAt:asc', label: 'Oldest sign-in' },
    { value: 'displayName:asc', label: 'Name A–Z' },
    { value: 'displayName:desc', label: 'Name Z–A' },
  ],
  resultNoun: ['user', 'users'],
};

/**
 * Column-header sort bindings, synced to the same `sort` param. The Name header
 * sorts on `displayName` (the backend's allow-listed field); the Last-login
 * header sorts on `lastLoginAt`.
 */
export const USERS_SORT_COLUMNS: Record<'displayName' | 'lastLoginAt', SortColumnBinding> = {
  displayName: { field: 'displayName', firstClickDir: 'asc' },
  lastLoginAt: { field: 'lastLoginAt', firstClickDir: 'desc' },
};

/**
 * URL search-param contract for the Users list, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/system/users` opens on the configured defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `lastLogin` is the date-range facet's PRESET key (e.g.
 * `last7`), distinct from the concrete custom bounds `lastLoginFrom`/
 * `lastLoginTo`; omitting it would stop a "Last 7 days" choice from sticking.
 * Kept beside the config (not in the route) so this boundary is unit-testable
 * without loading the route's React/router side effects.
 */
export const usersSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  role: z.string().optional(),
  lastLogin: z.string().optional(),
  lastLoginFrom: z.string().optional(),
  lastLoginTo: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
